'use client';

import { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';


interface ServiceStatus {
  id: string;
  name: string;
  url: string;
  status: 'operational' | 'degraded' | 'down' | 'maintenance';
  responseTime: number;
  uptime: number;
  lastChecked: string;
  category: string;
}

interface AlertThreshold {
  id: string;
  metric: string;
  operator: '>' | '<' | '>=' | '<=';
  value: number;
  unit: string;
  severity: 'warning' | 'critical';
  enabled: boolean;
}

interface HealthAlert {
  id: string;
  timestamp: string;
  service: string;
  message: string;
  severity: 'warning' | 'critical' | 'info';
  resolved: boolean;
}

const DEFAULT_SERVICES: ServiceStatus[] = [
  { id: 's1', name: 'Main Website', url: 'https://travelgate7783.builtwithrocket.new', status: 'operational', responseTime: 142, uptime: 99.98, lastChecked: new Date().toISOString(), category: 'Frontend' },
  { id: 's2', name: 'Flight Search API', url: '/api/flight-search', status: 'operational', responseTime: 287, uptime: 99.91, lastChecked: new Date().toISOString(), category: 'API' },
  { id: 's3', name: 'Booking Engine', url: '/api/booking', status: 'operational', responseTime: 198, uptime: 99.95, lastChecked: new Date().toISOString(), category: 'API' },
  { id: 's4', name: 'AI Recommendations', url: '/api/anthropic/travel-recommendations', status: 'operational', responseTime: 1240, uptime: 99.72, lastChecked: new Date().toISOString(), category: 'AI' },
  { id: 's5', name: 'Price Monitoring', url: '/api/anthropic/price-monitoring', status: 'operational', responseTime: 320, uptime: 99.88, lastChecked: new Date().toISOString(), category: 'AI' },
  { id: 's6', name: 'Visa Analysis', url: '/api/anthropic/visa-urgency-analysis', status: 'operational', responseTime: 980, uptime: 99.65, lastChecked: new Date().toISOString(), category: 'AI' },
  { id: 's7', name: 'Email Service', url: '/api/email', status: 'degraded', responseTime: 1850, uptime: 97.42, lastChecked: new Date().toISOString(), category: 'Notifications' },
  { id: 's8', name: 'Push Notifications', url: '/api/push', status: 'operational', responseTime: 210, uptime: 99.80, lastChecked: new Date().toISOString(), category: 'Notifications' },
  { id: 's9', name: 'Analytics Tracker', url: '/api/analytics', status: 'operational', responseTime: 95, uptime: 99.99, lastChecked: new Date().toISOString(), category: 'Analytics' },
  { id: 's10', name: 'PNR Dashboard', url: '/live-pnr-dashboard', status: 'operational', responseTime: 175, uptime: 99.93, lastChecked: new Date().toISOString(), category: 'Frontend' },
  { id: 's11', name: 'Support Ticketing', url: '/support', status: 'operational', responseTime: 155, uptime: 99.97, lastChecked: new Date().toISOString(), category: 'Support' },
  { id: 's12', name: 'Database (Supabase)', url: 'https://dummy.supabase.co', status: 'maintenance', responseTime: 0, uptime: 98.50, lastChecked: new Date().toISOString(), category: 'Database' },
];

const DEFAULT_THRESHOLDS: AlertThreshold[] = [
  { id: 't1', metric: 'Response Time', operator: '>', value: 2000, unit: 'ms', severity: 'warning', enabled: true },
  { id: 't2', metric: 'Response Time', operator: '>', value: 5000, unit: 'ms', severity: 'critical', enabled: true },
  { id: 't3', metric: 'Uptime', operator: '<', value: 99, unit: '%', severity: 'warning', enabled: true },
  { id: 't4', metric: 'Uptime', operator: '<', value: 95, unit: '%', severity: 'critical', enabled: true },
  { id: 't5', metric: 'Error Rate', operator: '>', value: 5, unit: '%', severity: 'warning', enabled: true },
  { id: 't6', metric: 'Error Rate', operator: '>', value: 15, unit: '%', severity: 'critical', enabled: true },
];

const DEMO_ALERTS: HealthAlert[] = [
  { id: 'a1', timestamp: new Date(Date.now() - 15 * 60000).toISOString(), service: 'Email Service', message: 'Response time exceeded 1500ms threshold (current: 1850ms)', severity: 'warning', resolved: false },
  { id: 'a2', timestamp: new Date(Date.now() - 45 * 60000).toISOString(), service: 'Database (Supabase)', message: 'Scheduled maintenance window started', severity: 'info', resolved: false },
  { id: 'a3', timestamp: new Date(Date.now() - 2 * 3600000).toISOString(), service: 'AI Recommendations', message: 'Uptime dropped below 99.8% threshold', severity: 'warning', resolved: true },
  { id: 'a4', timestamp: new Date(Date.now() - 5 * 3600000).toISOString(), service: 'Flight Search API', message: 'Spike in error rate: 8.2% (threshold: 5%)', severity: 'warning', resolved: true },
];

const statusConfig = {
  operational: { label: 'Operational', color: 'text-green-600', bg: 'bg-green-50 border-green-200', dot: 'bg-green-500', badge: 'bg-green-100 text-green-700' },
  degraded: { label: 'Degraded', color: 'text-yellow-600', bg: 'bg-yellow-50 border-yellow-200', dot: 'bg-yellow-500 animate-pulse', badge: 'bg-yellow-100 text-yellow-700' },
  down: { label: 'Down', color: 'text-red-600', bg: 'bg-red-50 border-red-200', dot: 'bg-red-500 animate-pulse', badge: 'bg-red-100 text-red-700' },
  maintenance: { label: 'Maintenance', color: 'text-sky-600', bg: 'bg-sky-50 border-sky-200', dot: 'bg-sky-400', badge: 'bg-sky-100 text-sky-700' },
};

const SystemHealthInteractive = () => {
  const [services, setServices] = useState<ServiceStatus[]>(DEFAULT_SERVICES);
  const [alerts, setAlerts] = useState<HealthAlert[]>(DEMO_ALERTS);
  const [thresholds, setThresholds] = useState<AlertThreshold[]>(DEFAULT_THRESHOLDS);
  const [activeTab, setActiveTab] = useState<'overview' | 'services' | 'alerts' | 'thresholds'>('overview');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastChecked, setLastChecked] = useState(new Date());
  const [isChecking, setIsChecking] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('All');

  const simulateCheck = useCallback(() => {
    setIsChecking(true);
    setTimeout(() => {
      setServices(prev => prev.map(s => ({
        ...s,
        responseTime: s.status === 'maintenance' ? 0 : Math.max(50, s.responseTime + Math.floor((Math.random() - 0.5) * 80)),
        lastChecked: new Date().toISOString(),
      })));
      setLastChecked(new Date());
      setIsChecking(false);
    }, 1200);
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(simulateCheck, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, simulateCheck]);

  const operational = services.filter(s => s.status === 'operational').length;
  const degraded = services.filter(s => s.status === 'degraded').length;
  const down = services.filter(s => s.status === 'down').length;
  const maintenance = services.filter(s => s.status === 'maintenance').length;
  const overallUptime = (services.reduce((sum, s) => sum + s.uptime, 0) / services.length).toFixed(2);
  const avgResponse = Math.round(services.filter(s => s.status !== 'maintenance').reduce((sum, s) => sum + s.responseTime, 0) / services.filter(s => s.status !== 'maintenance').length);
  const activeAlerts = alerts.filter(a => !a.resolved).length;

  const overallStatus = down > 0 ? 'down' : degraded > 0 ? 'degraded' : maintenance > 0 ? 'maintenance' : 'operational';
  const overallStatusConfig = statusConfig[overallStatus];

  const categories = ['All', ...Array.from(new Set(services.map(s => s.category)))];
  const filteredServices = categoryFilter === 'All' ? services : services.filter(s => s.category === categoryFilter);

  const resolveAlert = (id: string) => {
    setAlerts(prev => prev.map(a => a.id === id ? { ...a, resolved: true } : a));
  };

  const toggleThreshold = (id: string) => {
    setThresholds(prev => prev.map(t => t.id === id ? { ...t, enabled: !t.enabled } : t));
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString('en-GB');
  };

  const getResponseColor = (ms: number) => {
    if (ms === 0) return 'text-sky-500';
    if (ms < 300) return 'text-green-600';
    if (ms < 1000) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="min-h-screen bg-surface pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                <Icon name="SignalIcon" size={20} className="text-white" />
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground">System Health</h1>
            </div>
            <p className="text-muted-foreground">Live uptime monitoring, server status, and alert management</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setAutoRefresh(v => !v)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                autoRefresh ? 'bg-green-50 text-green-700 border-green-200' : 'bg-white text-muted-foreground border-border'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
              {autoRefresh ? 'Live' : 'Paused'}
            </button>
            <button
              onClick={simulateCheck}
              disabled={isChecking}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-60"
            >
              <Icon name="ArrowPathIcon" size={16} className={isChecking ? 'animate-spin' : ''} />
              {isChecking ? 'Checking...' : 'Check Now'}
            </button>
          </div>
        </div>

        {/* Overall Status Banner */}
        <div className={`rounded-2xl border p-5 mb-8 ${overallStatusConfig.bg}`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`w-4 h-4 rounded-full ${overallStatusConfig.dot}`} />
              <div>
                <div className={`text-xl font-black ${overallStatusConfig.color}`}>
                  All Systems {overallStatusConfig.label}
                </div>
                <div className="text-sm text-muted-foreground mt-0.5">
                  Last checked: {lastChecked.toLocaleTimeString('en-GB')} · {services.length} services monitored
                </div>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <div className="text-center">
                <div className="text-2xl font-black text-green-600">{operational}</div>
                <div className="text-xs text-muted-foreground">Operational</div>
              </div>
              {degraded > 0 && (
                <div className="text-center">
                  <div className="text-2xl font-black text-yellow-600">{degraded}</div>
                  <div className="text-xs text-muted-foreground">Degraded</div>
                </div>
              )}
              {down > 0 && (
                <div className="text-center">
                  <div className="text-2xl font-black text-red-600">{down}</div>
                  <div className="text-xs text-muted-foreground">Down</div>
                </div>
              )}
              {maintenance > 0 && (
                <div className="text-center">
                  <div className="text-2xl font-black text-sky-600">{maintenance}</div>
                  <div className="text-xs text-muted-foreground">Maintenance</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-border p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Overall Uptime</span>
              <Icon name="ArrowTrendingUpIcon" size={16} className="text-green-500" />
            </div>
            <div className="text-3xl font-black text-green-600">{overallUptime}%</div>
            <div className="text-xs text-muted-foreground mt-1">30-day average</div>
          </div>
          <div className="bg-white rounded-xl border border-border p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Avg Response</span>
              <Icon name="ClockIcon" size={16} className="text-blue-500" />
            </div>
            <div className={`text-3xl font-black ${getResponseColor(avgResponse)}`}>{avgResponse}<span className="text-base font-normal">ms</span></div>
            <div className="text-xs text-muted-foreground mt-1">Across all services</div>
          </div>
          <div className="bg-white rounded-xl border border-border p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Active Alerts</span>
              <Icon name="BellAlertIcon" size={16} className={activeAlerts > 0 ? 'text-orange-500' : 'text-green-500'} />
            </div>
            <div className={`text-3xl font-black ${activeAlerts > 0 ? 'text-orange-600' : 'text-green-600'}`}>{activeAlerts}</div>
            <div className="text-xs text-muted-foreground mt-1">{alerts.filter(a => a.resolved).length} resolved</div>
          </div>
          <div className="bg-white rounded-xl border border-border p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Services</span>
              <Icon name="ServerIcon" size={16} className="text-purple-500" />
            </div>
            <div className="text-3xl font-black text-foreground">{services.length}</div>
            <div className="text-xs text-muted-foreground mt-1">{categories.length - 1} categories</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-border shadow-sm">
          <div className="flex border-b border-border overflow-x-auto">
            {(['overview', 'services', 'alerts', 'thresholds'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex items-center gap-2 px-5 py-4 font-bold text-sm transition-all whitespace-nowrap capitalize ${
                  activeTab === tab ? 'text-primary border-b-2 border-primary bg-primary/5' : 'text-muted-foreground hover:text-foreground hover:bg-surface'
                }`}
              >
                <Icon name={tab === 'overview' ? 'Squares2X2Icon' : tab === 'services' ? 'ServerIcon' : tab === 'alerts' ? 'BellAlertIcon' : 'AdjustmentsHorizontalIcon'} size={16} />
                {tab}
                {tab === 'alerts' && activeAlerts > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full font-bold">{activeAlerts}</span>
                )}
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <h3 className="font-heading text-lg font-bold text-foreground">Service Status Overview</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {services.map(service => {
                    const cfg = statusConfig[service.status];
                    return (
                      <div key={service.id} className={`rounded-xl border p-4 ${cfg.bg}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <span className={`w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0 ${cfg.dot}`} />
                            <div>
                              <div className="font-bold text-sm text-foreground">{service.name}</div>
                              <div className="text-xs text-muted-foreground">{service.category}</div>
                            </div>
                          </div>
                          <span className={`text-xs font-bold px-2 py-1 rounded-full ${cfg.badge}`}>{cfg.label}</span>
                        </div>
                        <div className="mt-3 flex items-center gap-4 text-xs">
                          <span className={`font-bold ${getResponseColor(service.responseTime)}`}>
                            {service.status === 'maintenance' ? 'N/A' : `${service.responseTime}ms`}
                          </span>
                          <span className="text-muted-foreground">Uptime: <span className="font-bold text-foreground">{service.uptime}%</span></span>
                          <span className="text-muted-foreground ml-auto">{formatTime(service.lastChecked)}</span>
                        </div>
                        <div className="mt-2 w-full bg-white/60 rounded-full h-1">
                          <div
                            className={`h-1 rounded-full ${service.uptime >= 99.9 ? 'bg-green-500' : service.uptime >= 99 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${Math.min(100, service.uptime)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Services Tab */}
            {activeTab === 'services' && (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <h3 className="font-heading text-lg font-bold text-foreground">All Services</h3>
                  <div className="flex flex-wrap gap-2">
                    {categories.map(cat => (
                      <button
                        key={cat}
                        onClick={() => setCategoryFilter(cat)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
                          categoryFilter === cat ? 'bg-primary text-white' : 'bg-surface text-muted-foreground hover:bg-primary/10 hover:text-primary'
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-surface">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Service</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Category</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Response</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Uptime</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Last Check</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredServices.map(service => {
                        const cfg = statusConfig[service.status];
                        return (
                          <tr key={service.id} className="hover:bg-surface/50 transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
                                <span className="font-medium text-foreground">{service.name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-xs bg-surface px-2 py-1 rounded font-medium text-muted-foreground">{service.category}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-xs font-bold px-2 py-1 rounded-full ${cfg.badge}`}>{cfg.label}</span>
                            </td>
                            <td className={`px-4 py-3 font-bold ${getResponseColor(service.responseTime)}`}>
                              {service.status === 'maintenance' ? '—' : `${service.responseTime}ms`}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-16 bg-surface rounded-full h-1.5">
                                  <div
                                    className={`h-1.5 rounded-full ${service.uptime >= 99.9 ? 'bg-green-500' : service.uptime >= 99 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                    style={{ width: `${Math.min(100, service.uptime)}%` }}
                                  />
                                </div>
                                <span className="text-xs font-bold text-foreground">{service.uptime}%</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-xs text-muted-foreground">{formatTime(service.lastChecked)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Alerts Tab */}
            {activeTab === 'alerts' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-lg font-bold text-foreground">Health Alerts</h3>
                  <span className="text-sm text-muted-foreground">{activeAlerts} active · {alerts.filter(a => a.resolved).length} resolved</span>
                </div>
                {alerts.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                      <Icon name="CheckCircleIcon" size={24} className="text-green-500" />
                    </div>
                    <p className="font-medium text-foreground">No alerts</p>
                    <p className="text-sm text-muted-foreground mt-1">All systems are healthy</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {alerts.map(alert => (
                      <div
                        key={alert.id}
                        className={`rounded-xl border p-4 ${
                          alert.resolved ? 'bg-surface border-border opacity-60' :
                          alert.severity === 'critical' ? 'bg-red-50 border-red-200' :
                          alert.severity === 'warning'? 'bg-yellow-50 border-yellow-200' : 'bg-sky-50 border-sky-200'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <Icon
                              name={alert.severity === 'critical' ? 'ExclamationCircleIcon' : alert.severity === 'warning' ? 'ExclamationTriangleIcon' : 'InformationCircleIcon'}
                              size={18}
                              className={alert.resolved ? 'text-muted-foreground' : alert.severity === 'critical' ? 'text-red-500' : alert.severity === 'warning' ? 'text-yellow-500' : 'text-sky-500'}
                            />
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-bold text-sm text-foreground">{alert.service}</span>
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                  alert.resolved ? 'bg-gray-100 text-gray-500' :
                                  alert.severity === 'critical' ? 'bg-red-100 text-red-700' :
                                  alert.severity === 'warning'? 'bg-yellow-100 text-yellow-700' : 'bg-sky-100 text-sky-700'
                                }`}>{alert.resolved ? 'Resolved' : alert.severity}</span>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">{alert.message}</p>
                              <p className="text-xs text-muted-foreground mt-1">{formatTime(alert.timestamp)}</p>
                            </div>
                          </div>
                          {!alert.resolved && (
                            <button
                              onClick={() => resolveAlert(alert.id)}
                              className="flex-shrink-0 px-3 py-1.5 bg-white border border-border rounded-lg text-xs font-bold text-foreground hover:bg-surface transition-colors"
                            >
                              Resolve
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Thresholds Tab */}
            {activeTab === 'thresholds' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-lg font-bold text-foreground">Alert Thresholds</h3>
                  <span className="text-sm text-muted-foreground">{thresholds.filter(t => t.enabled).length} active rules</span>
                </div>
                <div className="space-y-3">
                  {thresholds.map(threshold => (
                    <div key={threshold.id} className={`rounded-xl border p-4 transition-all ${
                      threshold.enabled ? 'bg-white border-border' : 'bg-surface border-border opacity-60'
                    }`}>
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                            threshold.severity === 'critical' ? 'bg-red-500' : 'bg-yellow-500'
                          }`} />
                          <div>
                            <div className="font-bold text-sm text-foreground">
                              {threshold.metric} {threshold.operator} {threshold.value}{threshold.unit}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              Trigger <span className={`font-bold ${
                                threshold.severity === 'critical' ? 'text-red-600' : 'text-yellow-600'
                              }`}>{threshold.severity}</span> alert when {threshold.metric.toLowerCase()} is {threshold.operator} {threshold.value}{threshold.unit}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => toggleThreshold(threshold.id)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                            threshold.enabled ? 'bg-primary' : 'bg-gray-300'
                          }`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            threshold.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="InformationCircleIcon" size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-700">Alert thresholds are evaluated every 30 seconds during live monitoring. Triggered alerts appear in the Alerts tab and can be resolved once the issue is addressed.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemHealthInteractive;
